"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import type { FollowController, FollowState } from "./types";

type RawMethods = Omit<FollowController, "enabled">;

type CtxValue = {
  state: FollowState;
  methods: RawMethods;
  /** Llamado por CopilotProvider con settings.mode === 'follow'. */
  setEnabled: (v: boolean) => void;
  enabledRef: { readonly current: boolean };
  /** Aborta cualquier flujo en curso (limpia overlay + invalida runId). */
  abort: () => void;
  /** Avisa al provider que la próxima navegación es interna (skill).
   *  Sin esto, un router.push aborta el follow. */
  expectNavigation: () => void;
};

const FollowContext = createContext<CtxValue | null>(null);

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const initialState: FollowState = {
  cursor: null,
  highlight: null,
  step: null,
  ripples: [],
};

let rippleCounter = 0;

/** Smooth-scroll target into view if not already visible. */
function scrollIntoViewIfNeeded(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  // Margen de 80px para que no quede pegado al borde
  const margin = 80;
  const outOfView =
    rect.top < margin ||
    rect.bottom > vh - margin ||
    rect.left < 0 ||
    rect.right > vw;
  if (outOfView) {
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }
}

export function FollowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FollowState>(initialState);
  const enabledRef = useRef(false);
  // Generación monotónica: cada vez que cambia (por interrupción / cambio de
  // ruta / fin de sesión) las llamadas en curso ven que su run.id quedó obsoleto
  // y abortan en silencio antes de tocar setState.
  const runIdRef = useRef(0);

  const setEnabled = useCallback((v: boolean) => {
    enabledRef.current = v;
    if (!v) {
      runIdRef.current += 1; // invalida cualquier flujo en vuelo
      setState(initialState);
    }
  }, []);

  /** Aborta un flujo en curso (limpia overlay + invalida runId). */
  const abort = useCallback(() => {
    if (
      !enabledRef.current &&
      state.cursor === null &&
      state.highlight === null &&
      state.step === null
    ) {
      return;
    }
    runIdRef.current += 1;
    enabledRef.current = false;
    setState(initialState);
  }, [state]);

  // Las skills que navegan internamente (navigateTab, etc.) deben llamar
  // `expectNavigation()` antes de hacer `router.push(...)`. Cada llamada
  // "consume" 1 cambio de ruta sin abortar. Después de N ms sin uso, expira.
  const expectedNavRef = useRef(0);
  const expectedNavExpiryRef = useRef(0);
  const expectNavigation = useCallback(() => {
    expectedNavRef.current += 1;
    expectedNavExpiryRef.current = Date.now() + 4000; // 4s para que ocurra
  }, []);

  // Cancelar follow al cambiar de ruta INICIADO por el usuario (el usuario se
  // fue a otra pestaña/tab durante la guía). Si el cambio fue iniciado por
  // una skill (expectNavigation), se consume el contador y NO se aborta.
  const pathname = usePathname();
  const prevPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevPathRef.current === null) {
      prevPathRef.current = pathname;
      return;
    }
    if (prevPathRef.current !== pathname) {
      prevPathRef.current = pathname;
      const expired = Date.now() > expectedNavExpiryRef.current;
      if (!expired && expectedNavRef.current > 0) {
        // Navegación esperada por una skill → consumir y seguir
        expectedNavRef.current -= 1;
        return;
      }
      // Navegación inesperada → abortar
      if (enabledRef.current) abort();
    }
  }, [pathname, abort]);

  /** Helper: dado un runId capturado al inicio, devuelve true si sigue válido. */
  const isAlive = (myRun: number) =>
    enabledRef.current && runIdRef.current === myRun;

  const methods: RawMethods = useMemo(
    () => ({
      async step(label, opts) {
        if (!enabledRef.current) return;
        const myRun = runIdRef.current;
        setState((s) => ({
          ...s,
          step: { label, spinner: opts?.spinner !== false },
        }));
        await sleep(opts?.durationMs ?? 700);
        if (!isAlive(myRun)) return;
      },

      async pointAt(selector, opts) {
        if (!enabledRef.current) return;
        if (typeof window === "undefined") return;
        const myRun = runIdRef.current;
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;
        scrollIntoViewIfNeeded(el);
        await sleep(280);
        if (!isAlive(myRun)) return;
        const rect = el.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        setState((s) => ({
          ...s,
          cursor: { x, y, label: opts?.label },
        }));
        await sleep(opts?.durationMs ?? 700);
        if (!isAlive(myRun)) return;
      },

      async highlight(selector, opts) {
        if (!enabledRef.current) return;
        if (typeof window === "undefined") return;
        const myRun = runIdRef.current;
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;
        scrollIntoViewIfNeeded(el);
        await sleep(280);
        if (!isAlive(myRun)) return;
        const rect = el.getBoundingClientRect();
        setState((s) => ({
          ...s,
          highlight: {
            x: rect.left,
            y: rect.top,
            w: rect.width,
            h: rect.height,
          },
        }));
        await sleep(opts?.durationMs ?? 1000);
        if (!isAlive(myRun)) return;
        setState((s) => ({ ...s, highlight: null }));
      },

      async clickAt(selector, opts) {
        if (!enabledRef.current) return;
        if (typeof window === "undefined") return;
        const myRun = runIdRef.current;
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;
        scrollIntoViewIfNeeded(el);
        await sleep(320);
        if (!isAlive(myRun)) return;
        const rect = el.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        // 1. Mover cursor al target con label
        setState((s) => ({
          ...s,
          cursor: { x, y, label: opts?.label, pressing: false },
        }));
        await sleep(550); // user tiene tiempo de seguir el cursor
        if (!isAlive(myRun)) return;

        // 2. Highlight del target ANTES del click (anuncia qué va a pasar)
        setState((s) => ({
          ...s,
          highlight: {
            x: rect.left,
            y: rect.top,
            w: rect.width,
            h: rect.height,
          },
        }));
        await sleep(280);
        if (!isAlive(myRun)) return;

        // 3. Click: cursor press + ripple simultáneos
        setState((s) => ({
          ...s,
          cursor: s.cursor ? { ...s.cursor, pressing: true } : null,
        }));
        rippleCounter += 1;
        const id1 = `r${rippleCounter}`;
        setState((s) => ({
          ...s,
          ripples: [...s.ripples, { id: id1, x, y }],
        }));
        await sleep(140);
        if (!isAlive(myRun)) return;

        // 4. Segundo ripple para refuerzo visual (doble pulso)
        rippleCounter += 1;
        const id2 = `r${rippleCounter}`;
        setState((s) => ({
          ...s,
          ripples: [...s.ripples, { id: id2, x, y }],
        }));

        // 5. Cursor vuelve a tamaño normal
        setState((s) => ({
          ...s,
          cursor: s.cursor ? { ...s.cursor, pressing: false } : null,
        }));

        // 6. Mantener visible el highlight hasta que opts.durationMs termine
        await sleep(opts?.durationMs ?? 850);
        if (!isAlive(myRun)) return;

        // 7. Cleanup
        setState((s) => ({
          ...s,
          ripples: s.ripples.filter(
            (r) => r.id !== id1 && r.id !== id2,
          ),
          highlight: null,
        }));
      },

      async realClickAt(selector, opts) {
        if (!enabledRef.current) return;
        if (typeof window === "undefined") return;
        const myRun = runIdRef.current;
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;
        scrollIntoViewIfNeeded(el);
        await sleep(320);
        if (!isAlive(myRun)) return;
        const rect = el.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        // 1. Mover cursor al target con label
        setState((s) => ({
          ...s,
          cursor: { x, y, label: opts?.label, pressing: false },
        }));
        await sleep(550);
        if (!isAlive(myRun)) return;

        // 2. Highlight del target ANTES del click
        setState((s) => ({
          ...s,
          highlight: {
            x: rect.left,
            y: rect.top,
            w: rect.width,
            h: rect.height,
          },
        }));
        await sleep(280);
        if (!isAlive(myRun)) return;

        // 3. Click visual: cursor press + ripple + DOM click real
        setState((s) => ({
          ...s,
          cursor: s.cursor ? { ...s.cursor, pressing: true } : null,
        }));
        rippleCounter += 1;
        const id1 = `r${rippleCounter}`;
        setState((s) => ({
          ...s,
          ripples: [...s.ripples, { id: id1, x, y }],
        }));

        // ← Dispara el evento real en el DOM para abrir modales, drawers, etc.
        el.click();

        await sleep(140);
        if (!isAlive(myRun)) return;

        // 4. Segundo ripple
        rippleCounter += 1;
        const id2 = `r${rippleCounter}`;
        setState((s) => ({
          ...s,
          ripples: [...s.ripples, { id: id2, x, y }],
        }));

        // 5. Cursor vuelve a tamaño normal
        setState((s) => ({
          ...s,
          cursor: s.cursor ? { ...s.cursor, pressing: false } : null,
        }));

        // 6. Espera para que el modal/UI termine de abrirse
        await sleep(opts?.durationMs ?? 1200);

        // 7. Cleanup
        setState((s) => ({
          ...s,
          ripples: s.ripples.filter(
            (r) => r.id !== id1 && r.id !== id2,
          ),
          highlight: null,
        }));
      },

      clear() {
        setState(initialState);
      },
    }),
    [],
  );

  const value = useMemo<CtxValue>(
    () => ({ state, methods, setEnabled, enabledRef, abort, expectNavigation }),
    [state, methods, setEnabled, abort, expectNavigation],
  );

  return (
    <FollowContext.Provider value={value}>{children}</FollowContext.Provider>
  );
}

export function useFollowInternal() {
  const ctx = useContext(FollowContext);
  if (!ctx) {
    throw new Error("useFollowInternal debe usarse dentro de FollowProvider");
  }
  return ctx;
}
